import { CSSProperties, FC, ReactNode, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useSetAtom } from "jotai";
import { altDataUpgradeDialogState } from "@/lib/store";
import { useAltDataAccess } from "src/hooks/useAltDataAccess";
import { getPulseEarnings, getPulseMacro, getPulseShortMovers, getPulseSignals } from "@/controllers/pulse";
import { getCommentaryLatest, getMIArticles } from "@/controllers/market-intelligence";
import { getStockMovesFallers, getStockMovesRisers } from "@/controllers/stock-moves";
import { MacroRow, PulseCategory, PulseEarningsRow, PulseItem, ShortMover } from "@/models/pulse";
import Skeleton from "@/components/skeleton/Skeleton";
import styles from "./MarketsPulse.module.scss";
import { DetailContent } from "./DetailDialog";
import { CategoryTag, earningsDetail, earningsWhen, itemDetail, macroDetail, shortMoverDetail, stripMarkdown } from "./details";
import { usePulseData } from "./usePulseData";
import { modalQuery } from "src/hooks/useModalLink";
import { CATEGORY_META, clockTime, oneLockedTeaser, outlierTabFetches, shownOutlierTab, pct, signed, sparkPath, stockHref, timeAgo } from "./utils";

type OpenFn = (content: DetailContent) => void;

// ---- shell -------------------------------------------------------------------

type PanelProps = {
    title: ReactNode;
    href?: string;
    hrefLabel?: string;
    /** What the panel shows and how it is chosen; lives in the header's info tooltip. */
    info?: ReactNode;
    children: ReactNode;
    className?: string;
    style?: CSSProperties;
};

export const Panel: FC<PanelProps> = ({ title, href, hrefLabel = "More ↗", info, children, className, style }) => (
    <section className={`${styles.panel} ${className || ""}`} style={style}>
        <div className={styles.panelHead}>
            <h2>{title}</h2>
            {info && (
                <Tooltip title={info} arrow placement="top">
                    <span className={styles.info} tabIndex={0} role="img" aria-label="About this panel">
                        <InfoOutlinedIcon fontSize="inherit" />
                    </span>
                </Tooltip>
            )}
            <span className={styles.spacer} />
            {href && (
                <Link href={href} className={styles.expand} title={hrefLabel.replace("↗", "").trim()}>
                    <span className={styles.expandText}>{hrefLabel.replace("↗", "").trim()}</span>↗
                </Link>
            )}
        </div>
        <div className={styles.panelBody}>{children}</div>
    </section>
);

const Loading = () => (
    <div style={{ padding: 14 }}>
        <Skeleton variant="rounded" width="100%" height="120px" loading />
    </div>
);

const Heat: FC<{ score: number }> = ({ score }) => {
    const level = Math.max(1, Math.min(5, Math.round(score / 20)));
    return (
        <span className={styles.heat} title={`Interest score ${Math.round(score)}/100`}>
            {[1, 2, 3, 4, 5].map((n) => (
                <i key={n} className={n <= level ? styles.on : undefined} />
            ))}
        </span>
    );
};

const tone = (direction: PulseItem["direction"]) => (direction === "up" ? styles.up : direction === "down" ? styles.down : "");

// ---- alt data behind the Advanced tier -----------------------------------------------

/** Opens the Alt Data upgrade dialog (create account / upgrade / get Advanced by tier). */
export const useUnlockAltData = () => {
    const setOpen = useSetAtom(altDataUpgradeDialogState);
    return () => setOpen(true);
};

/** The reader's click on a pulse item: its detail, or the upgrade dialog when it is locked. */
export const useOpenItem = (onOpen: OpenFn) => {
    const unlock = useUnlockAltData();
    return (item: PulseItem) => (item.locked ? unlock() : onOpen(itemDetail(item)));
};

const LockedRow: FC<{ onClick: () => void }> = ({ onClick }) => (
    <button className={`${styles.row} ${styles.lockedRow}`} onClick={onClick} title="Included with ORTEX Advanced">
        <span className={styles.rowMain}>
            <LockOutlinedIcon className={styles.lockIcon} />
            <span className={styles.rowName}>Alt data outlier</span>
        </span>
        <span className={`${styles.rowValue} ${styles.unlockLink}`}>Unlock</span>
        <span className={styles.rowSub}>An alternative-data reading far from its own history. Included with ORTEX Advanced.</span>
    </button>
);

const AltLockBanner: FC = () => {
    const unlock = useUnlockAltData();
    return (
        <button className={styles.lockBanner} onClick={unlock}>
            <LockOutlinedIcon className={styles.lockIcon} />
            <span>
                <b>Alt data is part of ORTEX Advanced.</b> See which companies these readings are about.
            </span>
            <span className={styles.unlockLink}>Unlock ↗</span>
        </button>
    );
};

// ---- biggest signals ------------------------------------------------------------

export const TopSignals: FC<{ universeId: number; onOpen: OpenFn }> = ({ universeId, onOpen }) => {
    const { data, loading } = usePulseData((signal) => getPulseSignals({ limit: 8, perCategory: 2, universeId }, signal), [universeId], { refreshMs: 2 * 60_000 });
    const rows = oneLockedTeaser(data?.rows || []);
    const open = useOpenItem(onOpen);
    return (
        <Panel
            title={
                <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--mp-amber)" strokeWidth="2.4" aria-hidden="true">
                        <path d="M13 2 4 14h7l-1 8 9-12h-7z" />
                    </svg>
                    Biggest signals right now
                </>
            }
            href="/market-intelligence"
            hrefLabel="All ↗"
            info="The most unusual readings across ORTEX's datasets: each is scored against its own history and fades as it ages. Re-ranked every few minutes."
        >
            {loading && !rows.length ? (
                <Loading />
            ) : rows.length ? (
                rows.map((item, i) => (
                    <button key={item.id} className={`${styles.signal} ${item.locked ? styles.lockedRow : ""}`} onClick={() => open(item)}>
                        <span className={styles.rank}>{i + 1}</span>
                        <h3>
                            {item.locked && <LockOutlinedIcon className={styles.lockIcon} />}
                            {item.headline}
                        </h3>
                        <span className={styles.signalValue}>
                            <span className={item.locked ? styles.unlockLink : tone(item.direction)}>{item.locked ? "Unlock" : item.valueText}</span>
                            <Heat score={item.score} />
                        </span>
                        <span className={styles.signalMeta}>
                            <CategoryTag category={item.category} />
                            <span>{timeAgo(item.timestamp)} ago</span>
                        </span>
                        {item.detail && <span className={styles.signalDetail}>{item.detail}</span>}
                    </button>
                ))
            ) : (
                <div className={styles.empty}>{data && (data as any).pending ? "Gathering today's signals…" : "Nothing unusual yet today."}</div>
            )}
        </Panel>
    );
};

// ---- per-category feeds (auto-hide when quiet) -----------------------------------

/** A dataset's rows from the signals feed; the body of a category panel or tab. */
const CategoryList: FC<{ rows: PulseItem[]; loading: boolean; onOpen: OpenFn; empty?: string; category?: PulseCategory }> = ({ rows, loading, onOpen, empty, category }) => {
    const unlock = useUnlockAltData();
    const altDataAccess = useAltDataAccess();
    // Alt data below Advanced: the rows arrive locked from the API; say why above them.
    const lockedAlt = category === "alt" && (rows.some((row) => row.locked) || altDataAccess === false);
    return loading && !rows.length ? (
        <Loading />
    ) : rows.length ? (
        <div className={styles.rows}>
            {lockedAlt && <AltLockBanner />}
            {rows.map((item) =>
                item.locked ? (
                    <LockedRow key={item.id} onClick={unlock} />
                ) : (
                    <button key={item.id} className={styles.row} onClick={() => onOpen(itemDetail(item))}>
                        <span className={styles.rowMain}>
                            {item.ticker && <span className={styles.ticker}>{item.ticker}</span>}
                            <span className={styles.rowName}>{item.company || item.headline}</span>
                        </span>
                        <span className={`${styles.rowValue} ${tone(item.direction)}`}>{item.valueText || "—"}</span>
                        <span className={styles.rowSub}>{item.ticker ? item.detail || item.headline : item.detail ? `${item.headline} · ${item.detail}` : item.headline}</span>
                    </button>
                )
            )}
        </div>
    ) : lockedAlt ? (
        <AltLockBanner />
    ) : (
        <div className={styles.empty}>{empty || "Nothing unusual today."}</div>
    );
};

const useCategory = (category: PulseCategory, universeId: number, limit: number, enabled = true) => {
    const { data, loading } = usePulseData((signal) => getPulseSignals({ limit, categories: [category], perCategory: 0, universeId }, signal), [category, universeId], { enabled });
    return { rows: data?.rows || [], loading, settled: !loading && !!data && !(data as any).pending };
};

export const CategoryPanel: FC<{ category: PulseCategory; title: string; info: string; universeId: number; limit?: number; order?: number; onOpen: OpenFn }> = ({ category, title, info, universeId, limit = 10, order, onOpen }) => {
    const { rows, loading, settled } = useCategory(category, universeId, limit);
    // Nothing to say: the panel steps aside and the row closes up.
    if (settled && rows.length === 0) return null;
    return (
        <Panel
            title={
                <>
                    <span className={`${styles.dot} ${styles[`dot-${category}`]}`} />
                    {title}
                </>
            }
            href={CATEGORY_META[category].href}
            info={info}
            style={{ order }}
        >
            <CategoryList rows={rows} loading={loading} onOpen={onOpen} category={category} />
        </Panel>
    );
};

// ---- short interest movers ----------------------------------------------------------

export const ShortMoversPanel: FC<{ universeId: number; order?: number; onOpen: OpenFn }> = ({ universeId, order, onOpen }) => {
    const { data, loading } = usePulseData((signal) => getPulseShortMovers({ limit: 10, direction: "up", universeId }, signal), [universeId], { refreshMs: 5 * 60_000 });
    const rows: ShortMover[] = data || [];
    if (!loading && rows.length === 0) return null;
    const weekly = rows.length > 0 && rows[0].siChangePp === null;
    return (
        <Panel
            title={
                <>
                    <span className={`${styles.dot} ${styles["dot-shorts"]}`} />
                    Short interest
                </>
            }
            href="/short-interest"
            style={{ order }}
            info={
                weekly ? "Biggest rises in short interest as a % of free float over the last week (index members). Shown outside the live short-interest window." : "Biggest rises today in short interest as a % of free float, against the last close."
            }
        >
            {loading && !rows.length ? (
                <Loading />
            ) : (
                <div className={styles.rows}>
                    {rows
                        .filter((row) => row.ticker)
                        .map((row) => (
                            <button key={`${row.ticker}-${row.securityId}`} className={styles.row} onClick={() => onOpen(shortMoverDetail(row))}>
                                <span className={styles.rowMain}>
                                    <span className={styles.ticker}>{row.ticker}</span>
                                    <span className={styles.rowName}>{row.companyName}</span>
                                </span>
                                <span className={`${styles.rowValue} ${styles.down}`}>{weekly ? signed(row.siChangeWeekPp, 1, "pp") : signed(row.siChangePp, 1, "pp")}</span>
                                <span className={styles.rowSub}>
                                    {weekly
                                        ? `Short interest ${pct(row.siPercentFfWeekAgo)} → ${pct(row.siPercentFf)} of free float this week${row.daysToCover === null ? "" : ` · ${row.daysToCover.toFixed(1)} days to cover`}`
                                        : `Short interest ${pct(row.siPercentFfPrev)} → ${pct(row.siPercentFf)} of free float${row.costToBorrow === null ? "" : ` · borrow ${pct(row.costToBorrow, 2)}`}`}
                                </span>
                            </button>
                        ))}
                </div>
            )}
        </Panel>
    );
};

// ---- macro: only what moved -----------------------------------------------------------

const MacroSpark: FC<{ values: number[]; up: boolean }> = ({ values, up }) => {
    const d = sparkPath(values, 64, 22);
    if (!d) return <span />;
    return (
        <span className={styles.spark} title="Last 30 sessions">
            <svg width="64" height="22" viewBox="0 0 64 22" aria-hidden="true">
                <path d={d} fill="none" stroke={up ? "var(--mp-up)" : "var(--mp-down)"} strokeWidth="1.5" />
            </svg>
            <small>30d</small>
        </span>
    );
};

const macroMove = (row: MacroRow) => (row.kind === "yield" ? signed(row.changeBp, 0, "bp") : signed(row.changePct, 2, "%"));
const macroKind = (row: MacroRow) => (row.kind === "fx" ? "Currency" : row.kind === "commodity" ? "Commodity" : "Bond yield");

const MACRO_INFO = (threshold: number) => `Currencies, commodities and bond yields appear here only when the last session's move was at least ${threshold}× their normal daily move (60-session volatility). Quiet markets are listed underneath.`;

const MacroList: FC<{ onOpen: OpenFn }> = ({ onOpen }) => {
    const { data, loading } = usePulseData((signal) => getPulseMacro(signal), [], { refreshMs: 10 * 60_000 });
    const rows = data?.rows || [];
    const flagged = rows.filter((r) => r.flagged);
    const quiet = rows.filter((r) => !r.flagged && ["EURUSD", "GBPUSD", "USDJPY", "WFC_CME_GC1", "WFC_CME_CL1"].includes(r.code)).slice(0, 5);
    const threshold = data?.threshold ?? 1.5;
    if (loading && !rows.length) return <Loading />;
    return (
        <>
            <div className={styles.rows}>
                {flagged.slice(0, 8).map((row) => {
                    const move = row.kind === "yield" ? row.changeBp : row.changePct;
                    return (
                        <button key={row.code} className={`${styles.row} ${styles.macroRow}`} onClick={() => onOpen(macroDetail(row, threshold))}>
                            <span className={styles.rowMain}>
                                <span className={styles.rowTitle}>{row.name}</span>
                            </span>
                            <MacroSpark values={row.series} up={(move || 0) >= 0} />
                            <span className={`${styles.rowValue} ${(move || 0) >= 0 ? styles.up : styles.down}`}>{macroMove(row)}</span>
                            <span className={styles.rowSub}>
                                {macroKind(row)} · {Math.abs(row.sigma || 0).toFixed(1)}× a normal day
                            </span>
                        </button>
                    );
                })}
            </div>
            {flagged.length === 0 && <div className={styles.empty}>Currencies, commodities and yields are all moving normally.</div>}
            {quiet.length > 0 && (
                <div className={styles.chips} role="group" aria-label="Quiet markets">
                    {quiet.map((row) => (
                        <button key={row.code} className={styles.chip} onClick={() => onOpen(macroDetail(row, threshold))}>
                            {row.name} <span className={(row.kind === "yield" ? row.changeBp : row.changePct) >= 0 ? styles.up : styles.down}>{macroMove(row)}</span>
                        </button>
                    ))}
                </div>
            )}
        </>
    );
};

export const MacroPanel: FC<{ onOpen: OpenFn }> = ({ onOpen }) => (
    <Panel
        title={
            <>
                <span className={`${styles.dot} ${styles["dot-macro"]}`} />
                Macro moves
            </>
        }
        href="/currencies"
        hrefLabel="All ↗"
        style={{ order: 20 }}
        info={MACRO_INFO(1.5)}
    >
        <MacroList onOpen={onOpen} />
    </Panel>
);

// ---- research & news ----------------------------------------------------------------

/** Where a click on research lands: the Market Intelligence Articles tab, with the article open in its modal. */
const articleInMI = (id: number) => ({ pathname: "/market-intelligence", query: { tab: "ARTICLES", ...modalQuery("article", id) } });

export const ResearchPanel: FC = () => {
    const router = useRouter();
    const commentary = usePulseData((signal) => getCommentaryLatest("general", signal).catch(() => null), [], { refreshMs: 15 * 60_000 });
    const articles = usePulseData((signal) => getMIArticles({}, signal).catch(() => null), [], { refreshMs: 10 * 60_000 });
    const lead = commentary.data;
    const heads = (articles.data?.rows || []).filter((row) => row.id !== lead?.id).slice(0, 14);
    const summary = lead ? stripMarkdown(lead.body).slice(0, 260) : "";
    const openLead = () => lead && router.push(articleInMI(lead.id));
    // The headlines wait for the commentary: drawn first, they moved up or down when it took its place
    // above them (its height is not the placeholder's), and one was dropped if it was the lead (#1351).
    const waiting = commentary.loading && !lead;

    return (
        <Panel title="ORTEX Research & News" href="/market-intelligence" hrefLabel="All articles ↗" info="ORTEX's latest market commentary and Market Intelligence articles.">
            {waiting ? (
                <Loading />
            ) : lead ? (
                <button className={styles.lead} onClick={openLead}>
                    <span className={styles.kicker}>Market commentary · {clockTime(lead.publishedAt)}</span>
                    <h3>{lead.headline}</h3>
                    {summary && <p>{summary}…</p>}
                </button>
            ) : null}
            {!waiting &&
                heads.map((row) => (
                    <Link
                        key={row.id}
                        href={`/market-intelligence/articles/${row.id}`}
                        className={styles.headline}
                        onClick={(event) => {
                            // The href is the article's own page (crawlable, and what ctrl/middle-click opens);
                            // a plain click reads it in the Market Intelligence modal instead.
                            if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                            event.preventDefault();
                            router.push(articleInMI(row.id));
                        }}
                    >
                        <span className={styles.faint}>{clockTime(row.publishedAt)}</span>
                        <span>{row.headline}</span>
                        {row.ticker ? <span className={styles.ticker}>{row.ticker}</span> : <span />}
                    </Link>
                ))}
        </Panel>
    );
};

// ---- earnings -----------------------------------------------------------------------

const EARNINGS_INFO = "The largest companies reporting in the next week, with the move the options market is pricing for the report (at-the-money straddle) and current short interest.";

const useEarnings = (universeId: number, enabled = true) => {
    const { data, loading } = usePulseData((signal) => getPulseEarnings({ limit: 16, universeId }, signal), [universeId], { refreshMs: 30 * 60_000, enabled });
    return { rows: data?.rows || [], loading: loading || !!data?.pending, settled: !loading && !!data && !data.pending };
};

const EarningsList: FC<{ rows: PulseEarningsRow[]; loading: boolean; onOpen: OpenFn }> = ({ rows, loading, onOpen }) =>
    loading && !rows.length ? (
        <Loading />
    ) : rows.length ? (
        <div className={styles.rows}>
            {rows.map((row) => (
                <button key={row.eventId} className={styles.row} onClick={() => onOpen(earningsDetail(row))}>
                    <span className={styles.rowMain}>
                        <span className={styles.ticker}>{row.ticker}</span>
                        <span className={styles.rowName}>{earningsWhen(row)}</span>
                    </span>
                    <span className={styles.rowValue} title="Options-implied move">
                        {row.impliedMovePct === null ? "" : `±${row.impliedMovePct.toFixed(1)}%`}
                    </span>
                    <span className={styles.rowSub}>
                        {row.company}
                        {row.siPercentFf === null ? "" : ` · short interest ${pct(row.siPercentFf)}`}
                    </span>
                </button>
            ))}
        </div>
    ) : (
        <div className={styles.empty}>No large reporters in the week ahead.</div>
    );

export const EarningsPanel: FC<{ universeId: number; onOpen: OpenFn }> = ({ universeId, onOpen }) => {
    const { rows, loading, settled } = useEarnings(universeId);
    if (settled && rows.length === 0) return null;
    return (
        <Panel
            title={
                <>
                    <span className={`${styles.dot} ${styles["dot-news"]}`} />
                    Earnings
                </>
            }
            href="/earnings"
            hrefLabel="Calendar ↗"
            style={{ order: 10 }}
            info={EARNINGS_INFO}
        >
            <EarningsList rows={rows} loading={loading} onOpen={onOpen} />
        </Panel>
    );
};

// ---- tabbed panel: earnings / alt data / macro sharing one column -------------------------

export type OutlierTab = "earnings" | "alt" | "macro";

const TAB_META: Record<OutlierTab, { label: string; short?: string; dot: string; href: string; hrefLabel: string }> = {
    earnings: { label: "Earnings", dot: "dot-news", href: "/earnings", hrefLabel: "Calendar ↗" },
    alt: { label: "Alt data", short: "Alt", dot: "dot-alt", href: "/alt-data", hrefLabel: "More ↗" },
    macro: { label: "Macro", dot: "dot-macro", href: "/currencies", hrefLabel: "All ↗" },
};

const ALT_INFO =
    "Alternative data (registrations, industry output, filings, web traffic and more) that is new and unusual: readings at least 2σ from their own history, and fresh records, streaks and year-on-year moves. At most three rows per type of data.";

/**
 * One full-height column with tabs instead of two or three half-height panels, which were
 * too small to read. Opens on ``initial`` (the more unusual of alt data and macro today).
 */
export const OutlierTabs: FC<{ tabs: OutlierTab[]; initial?: OutlierTab; universeId: number; onOpen: OpenFn }> = ({ tabs, initial, universeId, onOpen }) => {
    const [picked, setActive] = useState<OutlierTab>(initial && tabs.includes(initial) ? initial : tabs[0]);
    const [touched, setTouched] = useState(false);
    useEffect(() => {
        // Follow the day's ranking until the reader picks a tab themselves.
        if (!touched && initial && tabs.includes(initial)) setActive(initial);
    }, [initial, tabs, touched]);
    // Only the tabs this layout shows: on wide screens earnings has its own panel beside these tabs,
    // and fetching it here as well sent the earnings request twice.
    const fetches = outlierTabFetches(tabs);
    const alt = useCategory("alt", universeId, 10, fetches.alt);
    const earnings = useEarnings(universeId, fetches.earnings);
    // A tab picked earlier may not be among this layout's tabs: then the first tab is the one shown.
    const active = shownOutlierTab(picked, tabs);
    const meta = TAB_META[active];
    const info = active === "earnings" ? EARNINGS_INFO : active === "alt" ? ALT_INFO : MACRO_INFO(1.5);
    return (
        <section className={`${styles.panel} ${styles.tabPanel}`} style={{ order: 20 }}>
            <div className={styles.panelHead}>
                <div className={styles.tabs} role="tablist">
                    {tabs.map((tab) => (
                        <button
                            key={tab}
                            role="tab"
                            aria-selected={tab === active}
                            className={`${styles.tab} ${tab === active ? styles.tabActive : ""}`}
                            onClick={() => {
                                setTouched(true);
                                setActive(tab);
                            }}
                        >
                            <span className={`${styles.dot} ${styles[TAB_META[tab].dot]}`} />
                            <span className={TAB_META[tab].short ? styles.tabLong : undefined}>{TAB_META[tab].label}</span>
                            {TAB_META[tab].short && <span className={styles.tabShort}>{TAB_META[tab].short}</span>}
                        </button>
                    ))}
                </div>
                <Tooltip title={info} arrow placement="top">
                    <span className={`${styles.info} ${styles.tabInfo}`} tabIndex={0} role="img" aria-label="About this tab">
                        <InfoOutlinedIcon fontSize="inherit" />
                    </span>
                </Tooltip>
                <span className={styles.spacer} />
                <Link href={meta.href} className={styles.expand} title={meta.hrefLabel.replace("↗", "").trim()}>
                    <span className={styles.expandText}>{meta.hrefLabel.replace("↗", "").trim()}</span>↗
                </Link>
            </div>
            <div className={styles.panelBody} role="tabpanel">
                {active === "earnings" && <EarningsList rows={earnings.rows} loading={earnings.loading} onOpen={onOpen} />}
                {active === "alt" && <CategoryList rows={alt.rows} loading={alt.loading} onOpen={onOpen} category="alt" empty="No alt-data readings far from their history today." />}
                {active === "macro" && <MacroList onOpen={onOpen} />}
            </div>
        </section>
    );
};

// ---- movers (under the index chart) --------------------------------------------------

type Mover = { ticker: string; exchange: string; change: number };

const toMovers = (rows: any[]): Mover[] =>
    (rows || [])
        .map((row) => {
            const ticker = Array.isArray(row.ticker) ? row.ticker[0] : row.ticker;
            return { ticker, exchange: row.exchangeSymbol || row.exchange || row.exchangeCode || "", change: Number(row.priceChange ?? row.change ?? 0) };
        })
        .filter((m) => m.ticker);

const MOVER_WIDTH = 96;

/** Gainers over losers, the same number in each row, as many as fit the width. */
export const MoversStrip: FC<{ universeId: number }> = ({ universeId }) => {
    const ref = useRef<HTMLDivElement>(null);
    const [columns, setColumns] = useState(5);
    // US large and mid caps (100) rather than the whole region, which includes OTC listings.
    const moversUniverse = universeId === 7 ? 100 : universeId;
    const { data } = usePulseData(
        async () => {
            const params = { page_size: 12, page: 1, universe_id: moversUniverse, filter_options: {} } as any;
            const [risers, fallers] = await Promise.all([getStockMovesRisers(params), getStockMovesFallers(params)]);
            return { risers: toMovers(risers?.rows), fallers: toMovers(fallers?.rows) };
        },
        [moversUniverse],
        { refreshMs: 5 * 60_000 }
    );

    // On short screens gainers and losers share one line, giving the chart above the height. That is a
    // media query in the stylesheet (`.moversRows`), so it holds from the first paint: set from here
    // after mount, it changed the strip's height and moved everything around it (#1351).
    //
    // Whether the column count has been measured: the chips wait for it, or on a slow phone they were
    // laid out for the default count first and moved sideways when it was measured (#1351).
    const [measured, setMeasured] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el || typeof ResizeObserver === "undefined") {
            setMeasured(true);
            return;
        }
        const observer = new ResizeObserver(([entry]) => {
            const short = window.innerHeight < 820 && window.innerWidth >= 1280;
            setMeasured(true);
            const fit = Math.floor(entry.contentRect.width / MOVER_WIDTH);
            setColumns(Math.max(short ? 2 : 3, Math.min(12, short ? Math.floor(fit / 2) : fit)));
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const count = Math.min(columns, data?.risers.length || columns, data?.fallers.length || columns);
    const renderRow = (list: Mover[] | undefined) =>
        // One blank chip across the row until the movers arrive, so the strip doesn't grow and push the page.
        // One, not a chip per column: the column count is measured after the first paint, and blank chips
        // re-spaced to it moved sideways (#1351).
        !list || !measured
            ? [
                  <span key="blank" className={styles.mover} style={{ gridColumn: "1 / -1" }} aria-hidden="true">
                      <b>&nbsp;</b>
                  </span>,
              ]
            : list.slice(0, count).map((m) => {
                  const href = stockHref(m.exchange, m.ticker);
                  const chip = (
                      <span className={`${styles.mover} ${m.change >= 0 ? styles.moverUp : styles.moverDown}`}>
                          <b>{m.ticker}</b>
                          <span>{signed(m.change, 1, "%")}</span>
                      </span>
                  );
                  return href ? (
                      <Link key={m.ticker} href={href} className={styles.moverLink}>
                          {chip}
                      </Link>
                  ) : (
                      <span key={m.ticker}>{chip}</span>
                  );
              });

    return (
        <div className={styles.moversStrip} ref={ref}>
            <div className={styles.moversHead}>
                <span>Top movers</span>
                <Link href="/stock-movers" className={styles.expand}>
                    More ↗
                </Link>
            </div>
            <div className={styles.moversRows}>
                <div className={styles.moversGrid} style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}>
                    {renderRow(data?.risers)}
                </div>
                <div className={styles.moversGrid} style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}>
                    {renderRow(data?.fallers)}
                </div>
            </div>
        </div>
    );
};
